/** Stream transports for the preview gateway (mediamtx behind nginx).
 *
 * WebRTC/WHEP is the live transport: sub-second, for the server-encoded views
 * (Live / Detections, our own B-frame-free H.264) and for Original whenever
 * the camera's own bitstream can ride it. WebRTC cannot carry H.264 with
 * B-frames (RFC 6184), and H.265 only plays where the browser has a hardware
 * HEVC decoder (Safari, Chrome 136+), so Original falls back to low-latency HLS
 * for those streams. The gateway keeps every camera warm, so segments already
 * exist when a viewer opens one.
 *
 * See `direct.ts` for the relay transport: the same original bitstream pushed
 * down a WebSocket and decoded by WebCodecs onto a canvas, with no playlist to
 * poll and no segment to fill.
 */

import Hls from "hls.js";

export type StreamConnection = {
  pc: RTCPeerConnection | null;
  close: () => void;
};

/** True when this browser's WebRTC stack can receive `codec` ("h264", "h265"/"hevc"). */
export function webrtcCanPlay(codec: string | undefined): boolean {
  const c = (codec || "").toLowerCase();
  if (c !== "h265" && c !== "hevc") return true;
  const caps =
    typeof RTCRtpReceiver !== "undefined" && RTCRtpReceiver.getCapabilities
      ? RTCRtpReceiver.getCapabilities("video")
      : null;
  return !!caps?.codecs.some((x) => x.mimeType.toLowerCase() === "video/h265");
}

/** WHEP playback of `<path>` through the gateway proxy (`/webrtc/<path>/whep`). */
export async function connectWhep(
  path: string,
  video: HTMLVideoElement,
  signal?: AbortSignal,
): Promise<StreamConnection> {
  const pc = new RTCPeerConnection();
  let sessionUrl: string | null = null;

  // Any failure past this point must close pc, or the orphaned connection
  // keeps its ICE/DTLS session up and mediamtx holds a stale consumer.
  try {
    pc.addTransceiver("video", { direction: "recvonly" });
    pc.addTransceiver("audio", { direction: "recvonly" });

    pc.ontrack = (ev) => {
      const stream = ev.streams[0] ?? new MediaStream([ev.track]);
      video.srcObject = stream;
      void video.play().catch(() => undefined);
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitIceGathering(pc, 2500);

    const resp = await fetch(`/webrtc/${encodeURIComponent(path)}/whep`, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: pc.localDescription?.sdp ?? offer.sdp ?? "",
      signal,
    });
    if (!resp.ok) {
      throw new Error(`WebRTC signaling failed (${resp.status})`);
    }
    sessionUrl = resp.headers.get("location");
    const answerSdp = await resp.text();
    await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
  } catch (err) {
    try {
      pc.close();
    } catch {
      /* noop */
    }
    throw err;
  }

  return {
    pc,
    close: () => {
      try {
        pc.close();
      } catch {
        /* noop */
      }
      if (sessionUrl) {
        // Explicit teardown frees the mediamtx session immediately instead of
        // waiting for the ICE disconnect timeout.
        void fetch(sessionUrl, { method: "DELETE", keepalive: true }).catch(
          () => undefined,
        );
      }
      if (video.srcObject) {
        video.srcObject = null;
      }
    },
  };
}

/** Low-latency HLS playback of `<path>` (`/hls/<path>/index.m3u8`). */
export function connectHls(
  path: string,
  video: HTMLVideoElement,
  opts?: { replay?: boolean },
): StreamConnection {
  const replay = Boolean(opts?.replay);
  const hls = new Hls({
    lowLatencyMode: true,
    backBufferLength: replay ? 6 : 10,
    // Recorded replay is a fresh mux. Default liveSyncDurationCount=3 with
    // a 6 s TARGETDURATION aims 18 s behind the edge — a few seconds of
    // black after the gateway is already ready. Stay one part behind.
    liveSyncDurationCount: replay ? 1 : 3,
    liveMaxLatencyDurationCount: replay ? 4 : Infinity,
    // Catch-up rate for the seek-back-to-live trick below; the gateway's
    // 420 ms parts transfer ~2x real time, which is what lets 1.5x arrive.
    maxLiveSyncPlaybackRate: 1.5,
    manifestLoadingMaxRetry: replay ? 8 : 3,
    manifestLoadingRetryDelay: replay ? 200 : 1000,
    manifestLoadingMaxRetryTimeout: replay ? 1000 : 64000,
  });

  // Show the first byte that exists, then walk back to the live edge.
  // hls.js normally sets currentTime only once a whole FRAGMENT (= one
  // camera GOP, often 10 s) is buffered, so a live join lands mid-GOP and
  // stares at black for most of it. Seeking to the start of the buffered
  // range shows a picture in ~1-2 s and maxLiveSyncPlaybackRate closes the
  // gap smoothly. (Measured in the dsingest dev project: 8.6-21.7 s -> 1-2 s.)
  let seeked = false;
  hls.on(Hls.Events.BUFFER_APPENDED, () => {
    if (seeked || video.buffered.length === 0) return;
    const t = video.currentTime;
    const inRange =
      video.buffered.length > 0 &&
      t >= video.buffered.start(0) &&
      t <= video.buffered.end(video.buffered.length - 1);
    if (!inRange) {
      video.currentTime = video.buffered.start(0);
    }
    seeked = true;
    void video.play().catch(() => undefined);
  });
  // Early fatal errors are expected while a keyframe is awaited; retry.
  hls.on(Hls.Events.ERROR, (_ev, data) => {
    if (!data.fatal) return;
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
      window.setTimeout(() => hls.startLoad(), 1500);
    } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
      hls.recoverMediaError();
    }
  });

  hls.loadSource(`/hls/${encodeURIComponent(path)}/index.m3u8`);
  hls.attachMedia(video);

  return {
    pc: null,
    close: () => {
      try {
        hls.destroy();
      } catch {
        /* noop */
      }
    },
  };
}

function waitIceGathering(pc: RTCPeerConnection, timeoutMs: number): Promise<void> {
  if (pc.iceGatheringState === "complete") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      pc.removeEventListener("icegatheringstatechange", onChange);
      resolve();
    }, timeoutMs);
    const onChange = () => {
      if (pc.iceGatheringState === "complete") {
        window.clearTimeout(timer);
        pc.removeEventListener("icegatheringstatechange", onChange);
        resolve();
      }
    };
    pc.addEventListener("icegatheringstatechange", onChange);
  });
}
